import { useApolloClient } from '@apollo/react-hooks';
import { useCallback, useRef } from 'react';

import { getDwellTimeHelper } from '@confluence/analytics-web-client';

import { useSendAnalyticsEvents } from '../analytics/useSendAnalyticsEvents';
import type { DwellTimeHelperData, DwellTimeHelperType } from '../analytics/DwellTimeHelper';

import { type ConsumptionEventData, getConsumptionEventData } from './getConsumptionEventData';
import { mapDwellTimeHelperDataToConsumptionEvent } from './mappingContentData';
import type { ConsumptionViewedEvent } from './ConsumptionEvents';

export const useConsumptionEvents = () => {
	const client = useApolloClient();
	const { sendEvent } = useSendAnalyticsEvents();
	const dwellTimeHelperRef = useRef<DwellTimeHelperType | null>(null);

	const sendConsumptionEventOrHandleError = useCallback(
		(props: ConsumptionEventData, action: 'initiated' | 'viewed') => {
			void getConsumptionEventData(client, action, props).then((event) => {
				sendEvent(event);
				return event;
			});
		},
		[client, sendEvent],
	);

	const sendInitiatedEvent = useCallback(
		(props: ConsumptionEventData) => {
			sendConsumptionEventOrHandleError(props, 'initiated');
		},
		[sendConsumptionEventOrHandleError],
	);

	const sendViewedEvent = useCallback(
		(props: ConsumptionEventData) => {
			sendConsumptionEventOrHandleError(props, 'viewed');
		},
		[sendConsumptionEventOrHandleError],
	);

	const sendCompletedEvent = useCallback(
		(props: ConsumptionViewedEvent, dwellTimeHelperData: DwellTimeHelperData) => {
			if (dwellTimeHelperData.attributes.finalDwellEvent) {
				const event = mapDwellTimeHelperDataToConsumptionEvent(props, dwellTimeHelperData);
				sendEvent(event);
			}
		},
		[sendEvent],
	);

	const sendViewedEventAndCompletedEvent = useCallback(
		(props: ConsumptionEventData) => {
			const sendEventPromise = getConsumptionEventData(client, 'viewed', props).then((event) => {
				sendEvent(event);
				return event;
			});

			void Promise.all([
				sendEventPromise,
				getDwellTimeHelper() as Promise<typeof DwellTimeHelperType>,
			]).then(([event, DwellTimeHelper]) => {
				const dwellTimeHelper = new DwellTimeHelper(
					// TODO: need to fix typing here @amykhaliuk
					(callbackData) => sendCompletedEvent(event as ConsumptionViewedEvent, callbackData),
					event.data,
				);
				dwellTimeHelper.start();

				dwellTimeHelperRef.current = dwellTimeHelper;
			});
		},
		[client, sendEvent, sendCompletedEvent],
	);

	const stopDwellTimeTracker = useCallback(() => {
		if (dwellTimeHelperRef.current) {
			dwellTimeHelperRef.current.stop();
		}
	}, []);

	return {
		sendInitiatedEvent,
		sendViewedEvent,
		sendViewedEventAndCompletedEvent,
		sendCompletedEvent,
		stopDwellTimeTracker,
	};
};
